/**
 * SSE streaming + frontend tool interrupt/resume for ap.orchestrator (trufront parity).
 */

function partToDisplayString(part) {
	if (part == null) return ''
	if (typeof part === 'string') return part
	if (typeof part !== 'object') return ''
	if (typeof part.text === 'string') return part.text
	if (typeof part.reasoning === 'string') return part.reasoning
	if (part.type === 'reasoning' || part.type === 'summary_text') {
		if (Array.isArray(part.summary)) {
			return part.summary.map(s => partToDisplayString(s)).join('')
		}
	}
	return ''
}

/**
 * LangChain / GPT 5.x Responses API may emit content as strings or block arrays.
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeAgentContentToString(raw) {
	if (raw == null) return ''
	if (typeof raw === 'string') return raw
	if (Array.isArray(raw)) {
		return raw.map(partToDisplayString).join('')
	}
	return partToDisplayString(raw)
}

function parseSSEChunks(rawText) {
	const chunks = []
	const lines = rawText.split('\n')
	for (let i = 0; i < lines.length; i += 1) {
		const trimmed = lines[i].trim()
		if (!trimmed || !trimmed.startsWith('data: ')) {
			// eslint-disable-next-line no-continue
			continue
		}
		try {
			const jsonStr = trimmed.substring(6)
			if (jsonStr) {
				chunks.push(JSON.parse(jsonStr))
			}
		} catch (e) {
			// eslint-disable-next-line no-console
			console.warn('Failed to parse SSE chunk:', trimmed, e)
		}
	}
	return chunks
}

/**
 * step_complete assistant payloads: planner (callLlmWithTools) → thought; generateChat → main bubble.
 * @returns {null|{ appendThought: string }|{ replace: true, content: string }}
 */
export function tryAddAssistantContentFromStepComplete(chunk, accumulatedContent, hasReceivedChatChunk) {
	const role = chunk.message && chunk.message.role
	if (role !== 'assistant' || chunk.message.content == null) return null
	const content = normalizeAgentContentToString(chunk.message.content)
	if (!content || content.trim().startsWith('{')) return null

	const isFinalChat = chunk.message.meta && chunk.message.meta.agentChatFinal
	const isPlannerOnly = chunk.node === 'callLlmWithTools'

	if (isPlannerOnly) {
		return { appendThought: content }
	}

	if (chunk.node !== 'generateChat') return null

	if (hasReceivedChatChunk && !isFinalChat) return null

	const isDuplicate = accumulatedContent === content
		|| accumulatedContent.endsWith(content)
	if (isDuplicate) return null

	return { replace: true, content }
}

/**
 * @param {object} chunk - step_complete SSE event
 * @param {{ accumulatedContent: string, thoughtContent: string, hasReceivedChatChunk: boolean }} streamState
 * @returns {{ accumulatedContent?: string, thoughtContent?: string, hasReceivedChatChunk?: boolean, streamPatch?: object }}
 */
export function mergeStepCompleteStreamState(chunk, streamState) {
	const contentToAdd = tryAddAssistantContentFromStepComplete(
		chunk,
		streamState.accumulatedContent,
		streamState.hasReceivedChatChunk,
	)
	if (!contentToAdd) return {}

	let { accumulatedContent, thoughtContent } = streamState
	const { hasReceivedChatChunk } = streamState

	if (typeof contentToAdd === 'object' && contentToAdd.appendThought) {
		const t = contentToAdd.appendThought.trim()
		if (t && !thoughtContent.trim()) {
			thoughtContent = t
		}
		return {
			thoughtContent,
			streamPatch: { content: accumulatedContent, thought: thoughtContent },
		}
	}

	if (typeof contentToAdd === 'object' && contentToAdd.replace) {
		accumulatedContent = contentToAdd.content
	}

	return {
		accumulatedContent,
		thoughtContent,
		hasReceivedChatChunk,
		streamPatch: { content: accumulatedContent, thought: thoughtContent },
	}
}

async function readSseStream(response, {
	onStreamUpdate,
	onThreadCreated,
	onTranscript,
	onStepComplete,
}) {
	const reader = response.body.getReader()
	const textDecoder = new TextDecoder()
	let accumulatedContent = ''
	let thoughtContent = ''
	let hasReceivedChatChunk = false
	let sseLineBuffer = ''
	let toolRequest = null

	function processEventChunks(chunks) {
		for (let i = 0; i < chunks.length; i += 1) {
			const chunk = chunks[i]
			if (chunk.event === 'thread_created' && chunk.data && chunk.data.id) {
				if (onThreadCreated) onThreadCreated(chunk.data.id)
			}

			if (chunk.event === 'transcript' && chunk.data !== undefined && onTranscript) {
				onTranscript(chunk.data)
			}

			if (chunk.event === 'tool_request' && chunk.data) {
				toolRequest = chunk.data
			}

			if (chunk.event === 'step_chunk') {
				const data = normalizeAgentContentToString(chunk.data)
				if (!data || data.trim().startsWith('{')) {
					// eslint-disable-next-line no-continue
					continue
				}
				const kind = chunk.streamKind
					|| (chunk.node === 'callLlmWithTools' ? 'thought' : 'chat')
				if (kind === 'thought') {
					thoughtContent += data
					if (onStreamUpdate) {
						onStreamUpdate({ content: accumulatedContent, thought: thoughtContent })
					}
				} else {
					if (chunk.node && chunk.node !== 'generateChat') {
						// eslint-disable-next-line no-continue
						continue
					}
					const isFirstChat = !hasReceivedChatChunk
					if (isFirstChat) {
						hasReceivedChatChunk = true
					}
					accumulatedContent += data
					if (onStreamUpdate) {
						onStreamUpdate({
							content: accumulatedContent,
							thought: thoughtContent,
							...(isFirstChat && thoughtContent ? { collapseThought: true } : {}),
						})
					}
				}
			}

			if (chunk.event === 'step_complete' && onStepComplete) {
				const update = onStepComplete(chunk, {
					accumulatedContent,
					thoughtContent,
					hasReceivedChatChunk,
				})
				if (update && update.accumulatedContent !== undefined) {
					({ accumulatedContent } = update)
				}
				if (update && update.thoughtContent !== undefined) {
					({ thoughtContent } = update)
				}
				if (update && update.hasReceivedChatChunk !== undefined) {
					({ hasReceivedChatChunk } = update)
				}
				if (update && update.streamPatch && onStreamUpdate) {
					onStreamUpdate(update.streamPatch)
				}
			}
		}
	}

	// eslint-disable-next-line no-constant-condition
	while (true) {
		// eslint-disable-next-line no-await-in-loop
		const { done, value } = await reader.read()
		if (value && value.byteLength) {
			sseLineBuffer += textDecoder.decode(value, { stream: true })
		}

		let nl
		// eslint-disable-next-line no-cond-assign
		while ((nl = sseLineBuffer.indexOf('\n')) !== -1) {
			const line = sseLineBuffer.slice(0, nl)
			sseLineBuffer = sseLineBuffer.slice(nl + 1)
			processEventChunks(parseSSEChunks(`${line}\n`))
		}

		if (done) {
			if (sseLineBuffer.trim()) {
				processEventChunks(parseSSEChunks(sseLineBuffer))
			}
			break
		}
	}

	return {
		accumulatedContent,
		thoughtContent,
		toolRequest,
	}
}

async function fetchAgentStream(url, body, getToken, isRetry = false) {
	const sessionToken = await getToken(isRetry ? { forceRefresh: true } : {})
	const headers = { 'Content-Type': 'application/json' }
	if (sessionToken) {
		headers.Authorization = `Bearer ${sessionToken}`
	}
	const response = await fetch(url, {
		method: 'POST',
		headers,
		body: JSON.stringify(body),
	})

	if ((response.status === 401 || response.status === 403) && !isRetry) {
		return fetchAgentStream(url, body, getToken, true)
	}

	if (!response.ok) {
		throw new Error(`Agent request failed: ${response.status} ${response.statusText}`)
	}
	if (!response.body) {
		throw new Error('Streaming not supported')
	}
	return response
}

/**
 * Stream chat with automatic tool_request → execute → tool-results resume cycles.
 */
export async function streamAgentChatWithToolLoop({
	agentBaseUrl,
	getThreadId,
	initialBody,
	getToken,
	onStreamUpdate,
	onThreadCreated,
	onTranscript,
	onStepComplete,
	executeToolRequests,
}) {
	const base = agentBaseUrl.replace(/\/$/, '')
	let url = `${base}/agent/chat`
	let body = initialBody
	let thoughtContent = ''
	let passChatContent = ''

	const onPassStreamUpdate = (u) => {
		if (u.content !== undefined) passChatContent = u.content
		if (u.thought !== undefined) thoughtContent = u.thought
		if (onStreamUpdate) onStreamUpdate(u)
	}

	// eslint-disable-next-line no-constant-condition
	while (true) {
		// eslint-disable-next-line no-await-in-loop
		const response = await fetchAgentStream(url, body, getToken)
		passChatContent = ''
		// eslint-disable-next-line no-await-in-loop
		const pass = await readSseStream(response, {
			onStreamUpdate: onPassStreamUpdate,
			onThreadCreated,
			onTranscript,
			onStepComplete,
		})

		if (pass.accumulatedContent) {
			({ accumulatedContent: passChatContent } = pass)
		}
		if (pass.thoughtContent) {
			({ thoughtContent } = pass)
		}

		if (!pass.toolRequest || !pass.toolRequest.requests || !pass.toolRequest.requests.length) {
			return {
				accumulatedContent: passChatContent,
				thoughtContent,
			}
		}

		// eslint-disable-next-line no-await-in-loop
		const { results } = await executeToolRequests(pass.toolRequest)

		const activeThreadId = (typeof getThreadId === 'function' ? getThreadId() : null)
			|| (initialBody && initialBody.threadId)
		if (!activeThreadId) {
			throw new Error('Cannot resume agent: missing threadId after tool_request')
		}

		url = `${base}/agent/threads/${encodeURIComponent(activeThreadId)}/tool-results`
		body = { results }
	}
}

/**
 * Execute frontend tool requests from ap.orchestrator interrupt/resume.
 * @param {Array<{ toolCallId: string, name: string, arguments?: object }>} requests
 * @param {{ tools: Array<{ name: string, execute?: function }>, onToolStart?: function, onToolComplete?: function }} options
 * @returns {Promise<{ results: Array }>}
 */
export async function executeWebsitePageToolRequests(requests, options) {
	const { tools, onToolStart, onToolComplete } = options
	const results = []

	for (let i = 0; i < requests.length; i += 1) {
		const req = requests[i]
		if (!req || !req.toolCallId || !req.name) {
			// eslint-disable-next-line no-continue
			continue
		}

		const tool = tools.find(t => t.name === req.name)
		if (!tool || typeof tool.execute !== 'function') {
			results.push({
				toolCallId: req.toolCallId,
				success: false,
				error: `Unknown tool: ${req.name}`,
			})
			// eslint-disable-next-line no-continue
			continue
		}

		const args = (typeof req.arguments === 'object' && req.arguments !== null && !Array.isArray(req.arguments))
			? req.arguments
			: {}

		if (onToolStart) onToolStart(tool, args)

		try {
			// eslint-disable-next-line no-await-in-loop
			const observation = await tool.execute(args)
			results.push({
				toolCallId: req.toolCallId,
				success: true,
				observation,
			})
			if (onToolComplete) onToolComplete(tool, true, observation)
		} catch (err) {
			results.push({
				toolCallId: req.toolCallId,
				success: false,
				error: (err && err.message) || String(err),
			})
			if (onToolComplete) {
				onToolComplete(tool, false, { error: (err && err.message) || String(err) })
			}
		}
	}

	return { results }
}

export function mapToolsForAgentApi(tools) {
	return tools.map(t => ({
		name: t.name,
		description: t.description,
		widget: !!t.widget,
		schema: Object.fromEntries(Object.entries(t.schema || {}).map(([k, v]) => [
			k,
			{ ...v, optional: v.required !== true },
		])),
	}))
}
